/**
 * Browser microphone capture for the turn-based character call.
 *
 * WHY THIS IS NOT INSIDE THE PANEL.
 * Three of the failure modes a spoken turn has go wrong *before* any audio
 * exists -- the page is not a secure context, the browser has no
 * `MediaRecorder`, or it has one but supports no container the pipeline can
 * use. Those are properties of the browser and the origin, not of a call, so
 * they are answered here once and read by the UI to decide whether to offer a
 * Speak button at all. A panel that discovers them only after asking for the
 * microphone shows a permission prompt it already knew would be useless.
 *
 * THE SECURE-CONTEXT CASE IS THE ONE THAT ACTUALLY BITES.
 * On a plain-HTTP origin that is not localhost the browser does not deny
 * `getUserMedia` -- it does not define `navigator.mediaDevices` at all. The
 * failure is therefore indistinguishable from "this browser is too old" unless
 * `isSecureContext` is checked first, and the two have completely different
 * fixes: one is a deployment change (terminate TLS at nginx), the other is a
 * different browser. `describeCapability` separates them so the message names
 * the real fix. Nothing here weakens or works around that rule; it only
 * explains it.
 */

/** Why the microphone cannot be used, or `null` when it can. */
export type MicrophoneBlocker =
  | 'server'
  | 'insecure-context'
  | 'no-media-devices'
  | 'no-media-recorder'
  | 'no-supported-format';

export interface MicrophoneCapability {
  usable: boolean;
  blocker: MicrophoneBlocker | null;
  /** What an operator should be told, and what they can do about it. */
  message: string;
  /**
   * The container to record into, chosen from what this browser reports it can
   * produce. `''` means "let the browser pick": Safari before 17 has no
   * `isTypeSupported`, and refusing to record there because it will not answer a
   * question would be worse than reading the type off the resulting blob.
   */
  mimeType: string;
}

/**
 * Containers to record into, best first.
 *
 * Ordered by what the pipeline handles most cleanly rather than by quality:
 * Core transcribes with Whisper after a pydub/ffmpeg conversion step, and Opus
 * in WebM is both what Chrome and Firefox produce natively and the smallest
 * thing to push over the tunnel. `audio/mp4` is here for Safari, which produces
 * nothing else. Nothing is transcoded in the browser -- see `pickMimeType`.
 */
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
  'audio/mpeg',
] as const;

/**
 * The first candidate this browser says it can record.
 *
 * Deliberately no conversion path. Re-encoding in the browser would mean
 * decoding to PCM and shipping a WAV several times the size over the same
 * tunnel that already drops long uploads, to reach a backend that runs ffmpeg
 * anyway -- so the honest options are "record something the contract accepts" or
 * "say this browser cannot". Both are represented; silently sending a container
 * the backend will reject is not.
 */
export function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  // Missing on older Safari. Returning '' hands the choice to the browser and
  // the resulting `Blob.type` is read back at send time.
  if (typeof MediaRecorder.isTypeSupported !== 'function') return '';
  for (const candidate of CANDIDATE_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

/**
 * The file extension the Admin API and Core should see for a recorded blob.
 *
 * Core keys its container sniffing off the filename, so a Safari `audio/mp4`
 * turn uploaded as `turn.webm` is handed to ffmpeg as the wrong thing. The
 * extension is derived from the blob's own type rather than assumed.
 */
export function audioFileName(mimeType: string | undefined | null): string {
  const base = (mimeType || '').split(';')[0]?.trim().toLowerCase();
  switch (base) {
    case 'audio/mp4':
    case 'video/mp4':
      return 'turn.mp4';
    case 'audio/mpeg':
      return 'turn.mp3';
    case 'audio/ogg':
      return 'turn.ogg';
    case 'audio/wav':
    case 'audio/x-wav':
      return 'turn.wav';
    case 'audio/webm':
    case 'video/webm':
      return 'turn.webm';
    default:
      // Unknown but non-empty types still have to be called something. WebM is
      // what every MediaRecorder that does not announce itself produces.
      return 'turn.webm';
  }
}

/**
 * Whether this page can record at all, and why not when it cannot.
 *
 * Safe to call during render and on the server: the `server` blocker exists so
 * a component can render the same disabled control on both sides of hydration
 * rather than flashing an enabled button that turns out to be unusable.
 */
export function describeCapability(): MicrophoneCapability {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      usable: false,
      blocker: 'server',
      message: 'Checking whether this browser can record…',
      mimeType: '',
    };
  }

  // Checked before `mediaDevices`, because an insecure origin is *why*
  // `mediaDevices` is missing and the fix is a deployment one.
  if (window.isSecureContext === false) {
    return {
      usable: false,
      blocker: 'insecure-context',
      message:
        `This page is served from ${window.location.origin}, which is not a secure context, and ` +
        'browsers only expose a microphone on one. There is no way to opt out of that from the ' +
        'page and nothing here tries to. Use http://localhost for local work, or put the ' +
        'deployment behind HTTPS — the nginx in deploy/ terminates TLS on :443 once a ' +
        'certificate is present.',
      mimeType: '',
    };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      usable: false,
      blocker: 'no-media-devices',
      message:
        'This browser exposes no microphone API (navigator.mediaDevices.getUserMedia) on this ' +
        'page, and the origin is already secure — so this is the browser rather than the ' +
        'deployment. A current Chrome, Edge, Firefox or Safari can hold a call.',
      mimeType: '',
    };
  }

  if (typeof MediaRecorder === 'undefined') {
    return {
      usable: false,
      blocker: 'no-media-recorder',
      message:
        'This browser can open a microphone but cannot record from one: it has no MediaRecorder. ' +
        'A spoken turn has to be captured whole before it is sent, so the call cannot work here.',
      mimeType: '',
    };
  }

  const mimeType = pickMimeType();
  if (mimeType === null) {
    return {
      usable: false,
      blocker: 'no-supported-format',
      message:
        'This browser records no audio container the pipeline accepts (WebM/Opus, Ogg, MP4 or ' +
        'MP3). Nothing is transcoded in the browser on purpose, so the recording would be ' +
        'rejected rather than silently converted into something unusable.',
      mimeType: '',
    };
  }

  return { usable: true, blocker: null, message: '', mimeType };
}

/** Turn a `getUserMedia` rejection into the specific thing that went wrong. */
export function describeGetUserMediaError(error: unknown): string {
  const name = (error as { name?: string })?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return (
        'Microphone permission was denied for this site. Allow it in the browser’s address-bar ' +
        'controls and press Speak again — the permission is only ever requested when you do.'
      );
    case 'SecurityError':
      return (
        'The browser blocked microphone access for this origin. That is the secure-context rule: ' +
        'use http://localhost locally, or serve the deployment over HTTPS.'
      );
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found. Connect one, then press Speak again.';
    case 'NotReadableError':
    case 'TrackStartError':
      return (
        'The microphone is present but could not be opened — another application usually has it. ' +
        'Close whatever is holding it and press Speak again.'
      );
    case 'OverconstrainedError':
      return 'No microphone matched the requested constraints.';
    case 'AbortError':
      return 'Opening the microphone was aborted before it started.';
    default:
      return `The microphone could not be opened: ${
        (error as { message?: string })?.message || String(error)
      }`;
  }
}
